import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

interface ScaleToFitProps {
  /** Intrinsic (unscaled) size of the content, in CSS px. */
  width: number;
  height: number;
  children: ReactNode;
  className?: string;
}

/**
 * Renders fixed-size content (a device mockup, a document page) at its real
 * size and scales it down to fit the parent, centered. Keeps an embedded
 * narrative at a true phone width so its layout never squeezes.
 */
export const ScaleToFit = ({
  width,
  height,
  children,
  className = "",
}: ScaleToFitProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => {
      const { width: w, height: h } = el.getBoundingClientRect();
      if (w && h) setScale(Math.min(w / width, h / height, 1));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [width, height]);

  return (
    <div ref={ref} className={`relative w-full h-full ${className}`}>
      <div
        className="absolute left-1/2 top-1/2"
        style={{
          width,
          height,
          transform: `translate(-50%, -50%) scale(${scale})`,
        }}
      >
        {children}
      </div>
    </div>
  );
};
